import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Heart, Sparkles as SparklesIcon, Loader2, ArrowRight, Camera } from "lucide-react";
import Sparkles from "../components/Sparkles";
import ShareButtons from "../components/ShareButtons";
import LangToggle from "../components/LangToggle";
import RoyalArrival from "../components/RoyalArrival";
import RoyalJourney from "../components/RoyalJourney";
import { fileUrl, getGuest } from "../lib/api";
import { useLang, t } from "../lib/i18n";

export default function Reveal() {
  const lang = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isKiosk = params.get("kiosk") === "1";
  const [guest, setGuest] = useState(null);
  const [lineIdx, setLineIdx] = useState(0);

  const processingLines = [
    t("rev_loading_1", lang),
    t("rev_loading_2", lang),
    t("rev_loading_3", lang),
  ];

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const g = await getGuest(id);
        if (cancelled) return;
        setGuest(g);
        if (g.status === "generating" || g.status === "pending") {
          setTimeout(tick, 2500);
        }
      } catch {
        setTimeout(tick, 3500);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const tm = setInterval(() => setLineIdx((i) => (i + 1) % 3), 2200);
    return () => clearInterval(tm);
  }, []);

  // Kiosk auto-cycle — once the guest's character is ready and they've seen
  // it for ~7 seconds, hand the device back to the kiosk thank-you screen
  // (which then waits 10 more seconds and reopens the form for the next guest).
  useEffect(() => {
    if (!isKiosk) return;
    if (!guest || (guest.status !== "ready" && guest.status !== "approved")) return;
    const tm = setTimeout(() => {
      const name = encodeURIComponent(guest.name || "");
      navigate(`/kiosk?thanks=1&name=${name}`, { replace: true });
    }, 7000);
    return () => clearTimeout(tm);
  }, [isKiosk, guest, navigate]);

  // Auto-return to /register 15 seconds after the reveal renders (non-kiosk
  // flow). Guests can dismiss the countdown if they want to stay and admire
  // their portrait / share.
  const AUTO_RETURN_SECS = 15;
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RETURN_SECS);
  const [autoPaused, setAutoPaused] = useState(false);
  useEffect(() => {
    if (isKiosk) return; // kiosk has its own flow
    if (!guest || (guest.status !== "ready" && guest.status !== "approved")) return;
    if (autoPaused) return;
    if (secondsLeft <= 0) {
      navigate("/register");
      return;
    }
    const tm = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(tm);
  }, [isKiosk, guest, autoPaused, secondsLeft, navigate]);

  const isLoading = !guest || guest.status === "generating" || guest.status === "pending";
  const failed = guest?.status === "failed";

  return (
    <div className="relative min-h-screen px-6 sm:px-12 py-12">
      <Sparkles count={48} />
      <div className="relative max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-between mb-8">
          <Link to="/welcome" className="label-eyebrow text-ink-soft hover:text-plum inline-block" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>
            ← {t("back", lang)}
          </Link>
          <LangToggle />
        </div>

        {isLoading && (
          <div data-testid="reveal-loading" className="py-16">
            <div className="mx-auto w-28 h-28 rounded-full glass-deep grid place-items-center mb-8 animate-float">
              <Loader2 className="text-[#B38728] animate-spin" size={42} />
            </div>
            <p className="font-script text-3xl sm:text-4xl gold-text" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>{processingLines[lineIdx]}</p>
            <p className="text-ink-soft mt-4 max-w-md mx-auto" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>
              {t("rev_loading_body", lang)}
            </p>
          </div>
        )}

        {failed && (
          <div data-testid="reveal-failed" className="py-16">
            <h1 className="font-display text-3xl text-plum" style={{ fontFamily: "'Noto Sans Telugu',serif" }}>{t("rev_failed", lang)}</h1>
            <p className="text-ink-soft mt-3">{guest?.error || "Please try again."}</p>
            <Link to="/register" className="btn-royal mt-6 inline-flex" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>
              <Camera size={16} /> {t("rev_retry", lang)}
            </Link>
          </div>
        )}

        {guest && (guest.status === "ready" || guest.status === "approved") && (
          <div data-testid="reveal-success" className="py-4">
            {/* Royal Family Arrived — proclamation card */}
            <RoyalArrival
              name={guest.name}
              roleKey={guest.relationship}
              lovingTitle={guest.loving_title || guest.role_title}
            />

            <p className="label-eyebrow mt-10" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("rev_certificate", lang)}</p>
            <h1 className="font-display text-4xl sm:text-6xl font-semibold text-ink mt-1 leading-tight" style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}>
              <span className="gold-text">{guest.loving_title || guest.role_title}</span>
            </h1>
            <p className="font-script text-3xl text-plum mt-2">{guest.name}</p>

            <div className="relative inline-block mt-8 w-full max-w-xl">
              <div
                className="absolute -inset-6 rounded-full opacity-50 blur-2xl"
                style={{ background: "radial-gradient(circle, #FCF6BA, transparent 70%)" }}
              />
              <div className="relative magical-card p-3" data-testid="reveal-portrait">
                <div className="overflow-hidden" style={{ borderRadius: "32px" }}>
                  <img src={fileUrl(guest.character_path)} alt={guest.name} className="w-full aspect-square object-cover" />
                </div>
              </div>
            </div>

            {guest.personal_message && (
              <div className="mt-8 max-w-2xl mx-auto glass-deep rounded-3xl p-6 sm:p-8 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <Heart size={14} className="text-[#C45A8A]" fill="#FFC4D6" />
                  <p className="label-eyebrow" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("rev_message_from", lang)}</p>
                </div>
                <p
                  className="font-display text-xl sm:text-2xl text-ink leading-relaxed italic"
                  style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}
                  lang="te"
                >
                  &ldquo;{guest.personal_message}&rdquo;
                </p>
                <p className="font-script text-3xl text-plum text-right mt-3">— Anagha ✦</p>
              </div>
            )}

            {/* Royal Journey — highlight step 3 (Celebrate) now that they're in */}
            <div className="mt-12 text-left">
              <RoyalJourney currentIndex={2} />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>
              <Link to="/dance" data-testid="reveal-gallery-btn" className="btn-royal">
                {t("rev_to_dance", lang)}
                <ArrowRight size={18} />
              </Link>
              <Link to="/register" className="btn-ghost-royal">
                <SparklesIcon size={16} />
                {t("rev_add_more", lang)}
              </Link>
            </div>

            <div className="mt-8">
              <p className="label-eyebrow mb-3" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("rev_share_label", lang)}</p>
              <ShareButtons
                imageUrl={fileUrl(guest.character_path)}
                shareUrl={typeof window !== "undefined" ? window.location.origin + "/gallery" : ""}
                filename={`${guest.name.replace(/\s+/g, "_")}-anagha-hall-of-love.png`}
                title={`${guest.name} — ${guest.loving_title || guest.role_title}`}
                text={`A keepsake from Anagha's Hall of Love ✨ "${guest.personal_message || ""}"`}
              />
            </div>
          </div>
        )}

        {/* Auto-return countdown (non-kiosk) — fixed bottom-center pill */}
        {!isKiosk && guest && (guest.status === "ready" || guest.status === "approved") && (
          <div
            data-testid="auto-return-pill"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center justify-center gap-2 px-4 py-2.5 rounded-full backdrop-blur"
            style={{
              background: "linear-gradient(140deg, rgba(74,22,53,0.94) 0%, rgba(43,10,32,0.96) 100%)",
              border: "1px solid rgba(212,175,55,0.5)",
              boxShadow: "0 12px 32px -10px rgba(74,22,53,0.55)",
              fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif",
              maxWidth: "min(94vw, 640px)",
            }}
          >
            <span className="text-xs sm:text-sm text-amber-100/90">
              {t("rev_next_guest_in", lang)}
            </span>
            <span
              data-testid="auto-return-seconds"
              className="inline-flex items-center justify-center min-w-[36px] h-[28px] px-2 rounded-full text-sm font-bold border"
              style={{
                background: autoPaused ? "rgba(255,255,255,0.06)" : "linear-gradient(180deg,#F6D77A,#D4AF37)",
                color: autoPaused ? "#FFE9A8" : "#3E2A0A",
                borderColor: autoPaused ? "rgba(212,175,55,0.35)" : "rgba(212,175,55,0.9)",
                opacity: autoPaused ? 0.5 : 1,
              }}
            >
              {autoPaused ? "⏸" : `${secondsLeft}s`}
            </span>
            <button
              type="button"
              onClick={() => { setAutoPaused(false); setSecondsLeft(0); }}
              className="text-xs sm:text-sm px-3 py-1 rounded-full border border-amber-300/45 bg-amber-500/15 text-amber-50 hover:bg-amber-500/25 transition-colors"
              data-testid="auto-return-next-btn"
            >
              ✦ {t("rev_next_guest_now", lang)}
            </button>
            <button
              type="button"
              onClick={() => setAutoPaused((p) => !p)}
              className="text-xs px-3 py-1 rounded-full border border-white/25 text-amber-100/80 hover:text-amber-50 hover:bg-white/10 transition-colors"
              data-testid="auto-return-pause-btn"
            >
              {autoPaused ? "▶" : "⏸"} {t("rev_stay_here", lang)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
